import React, { ReactNode } from 'react';
import { Box, Typography } from '@mui/material';
import MuiAccordion, { AccordionProps } from '@mui/material/Accordion';
import MuiAccordionSummary, {
  AccordionSummaryProps,
} from '@mui/material/AccordionSummary';
import MuiAccordionDetails from '@mui/material/AccordionDetails';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import { styled } from '@mui/material/styles';
import './converge-detail-accordion.scss';

const Accordion = styled((props: AccordionProps) => (
  <MuiAccordion disableGutters elevation={0} square {...props} />
))(() => ({
  backgroundColor: 'white',
  '&::before': {
    display: 'none',
  },
  marginBottom: '16px',
}));

const AccordionSummary = styled((props: AccordionSummaryProps) => (
  <MuiAccordionSummary {...props} sx={{ flexDirection: 'row' }} />
))(() => ({
  '& .MuiAccordionSummary-content': {
    margin: '8px 0',
  },
}));

const AccordionDetails = styled(MuiAccordionDetails)(() => ({
  padding: '16px',
  display: 'flex',
  flexWrap: 'wrap',
  gap: '16px',
}));

export interface ConvergeDetailAccordionProps {
  /** Unique identifier for the accordion */
  id?: string;
  /** Whether the accordion is expanded */
  expanded: boolean;
  /** Callback when accordion expand/collapse state changes */
  onChange: (event: React.SyntheticEvent, isExpanded: boolean) => void;
  /** Title text displayed in the accordion header */
  title: string;
  /** Description text displayed below the title */
  description?: string;
  /** Content to display in the accordion body */
  children: ReactNode;
  /** Additional CSS class name */
  className?: string;
  /** Additional styles for AccordionDetails */
  detailsClassName?: string;
  /** Custom padding bottom for the accordion */
  paddingBottom?: string;
  /** Test ID for testing purposes */
  testId?: string;
}

export const ConvergeDetailAccordion: React.FC<
  ConvergeDetailAccordionProps
> = ({
  id,
  expanded,
  onChange,
  title,
  description,
  children,
  className = '',
  detailsClassName = '',
  paddingBottom = '30px',
  testId,
}) => {
  return (
    <div
      className={`converge-detail-accordion ${className}`}
      data-testid={testId}
    >
      <Accordion
        id={id}
        expanded={expanded}
        onChange={onChange}
        sx={{ paddingBottom }}
      >
        <AccordionSummary>
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              {expanded ? (
                <KeyboardArrowDownIcon />
              ) : (
                <KeyboardArrowRightIcon />
              )}
              <Typography variant="h5" className="box-title">
                {title}
              </Typography>
            </Box>
            {description && (
              <Box className="description-title">{description}</Box>
            )}
          </Box>
        </AccordionSummary>
        <AccordionDetails className={detailsClassName}>
          {children}
        </AccordionDetails>
      </Accordion>
    </div>
  );
};

export default ConvergeDetailAccordion;
